import React, { useEffect, useRef, useCallback } from 'react';
import { MapContainer as LeafletMap, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import type { Location } from '@/types';

// Fix for default markers in React-Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

interface MapContainerProps {
  center: Location;
  zoom?: number;
  height?: string;
  className?: string;
  children?: React.ReactNode;
  onLocationSelect?: (location: Location) => void;
  onZoomChange?: (zoomLevel: number) => void;
  onBoundsChange?: (bounds: {
    south: number;
    north: number;
    west: number;
    east: number;
  }) => void;
  onUserDrag?: () => void;
  shouldUpdateView?: boolean; // New prop to control when map should update
}

/**
 * Component to handle map updates only when location actually changes
 */
function MapUpdater({ 
  center, 
  zoom = 13, 
  shouldUpdate = false 
}: { 
  center: Location; 
  zoom?: number; 
  shouldUpdate?: boolean;
}) {
  const map = useMap();
  const lastCenter = useRef<Location | null>(null);

  useEffect(() => {
    // Only update map view if explicitly told to, or if it's a significant location change
    if (shouldUpdate) {
      map.setView([center.latitude, center.longitude], zoom);
      lastCenter.current = center;
    } else if (!lastCenter.current) {
      // First time initialization
      map.setView([center.latitude, center.longitude], zoom);
      lastCenter.current = center;
    }
  }, [map, center, zoom, shouldUpdate]);

  return null;
}

/**
 * Component to handle map events (click, zoom, bounds changes)
 */
function MapEventHandler({
  onLocationSelect,
  onZoomChange,
  onBoundsChange,
  onUserDrag,
}: {
  onLocationSelect?: (location: Location) => void;
  onZoomChange?: (zoomLevel: number) => void;
  onBoundsChange?: (bounds: {
    south: number;
    north: number;
    west: number;
    east: number;
  }) => void;
  onUserDrag?: () => void;
}) {
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const handleBoundsChange = useCallback((map: L.Map) => {
    // Clear existing timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Debounce bounds changes to prevent excessive API calls
    debounceRef.current = setTimeout(() => {
      // Always sync zoom from Leaflet so mapZoom is current for pan-only moves.
      // zoomend only fires on zoom changes, not pans — without this, mapZoom can
      // stay at its initial value (6) and cause DBSCAN to run at the wrong zoom level.
      if (onZoomChange) onZoomChange(map.getZoom());
      if (onBoundsChange) {
        const bounds = map.getBounds();
        onBoundsChange({
          south: bounds.getSouth(),
          north: bounds.getNorth(),
          west: bounds.getWest(),
          east: bounds.getEast(),
        });
      }
    }, 300); // 300ms debounce
  }, [onBoundsChange, onZoomChange]);

  const map = useMapEvents({
    click: (e) => {
      if (onLocationSelect) {
        onLocationSelect({
          latitude: e.latlng.lat,
          longitude: e.latlng.lng,
        });
      }
    },
    zoomend: () => {
      if (onZoomChange) {
        onZoomChange(map.getZoom());
      }
      handleBoundsChange(map);
    },
    moveend: () => {
      handleBoundsChange(map);
    },
    dragend: () => {
      onUserDrag?.();
    },
    // Also trigger on initial load
    load: () => {
      if (onZoomChange) {
        onZoomChange(map.getZoom());
      }
      handleBoundsChange(map);
    },
  });

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return null;
}

/**
 * Main map container component
 */
export const MapContainer: React.FC<MapContainerProps> = ({
  center,
  zoom = 13,
  height = '500px',
  className = '',
  children,
  onLocationSelect,
  onZoomChange,
  onBoundsChange,
  onUserDrag,
  shouldUpdateView = false,
}) => {
  const mapRef = useRef<L.Map | null>(null);

  return (
    <div className={`relative ${className}`} style={{ height }}>
      <LeafletMap
        center={[center.latitude, center.longitude]}
        zoom={zoom}
        style={{ height: '100%', width: '100%' }}
        ref={mapRef}
        className="rounded-lg"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          maxZoom={18}
        />

        <MapUpdater center={center} zoom={zoom} shouldUpdate={shouldUpdateView} />

        <MapEventHandler
          onLocationSelect={onLocationSelect}
          onZoomChange={onZoomChange}
          onBoundsChange={onBoundsChange}
          onUserDrag={onUserDrag}
        />
        
        {children}
      </LeafletMap>
    </div>
  );
};

export default MapContainer;